/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import {
  propTypes,
  SHOW_SEARCH_BAR,
  SIGNUP_TAB_DETAIL,
  SIGNUP_TAB_COMPLETED,
  BUSINESS_LISTING_TYPE_PUBLISHED,
} from '../../../../util/types';
import { ensureCurrentUser, getBusinessListingParams } from '../../../../util/data';

import {
  Button,
  NamedLink,
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  // NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    businessListing,
    // currentUserHasListings,
    // notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onResendVerificationEmail,
    sendVerificationEmailInProgress
  } = props;

  const user = ensureCurrentUser(currentUser);
  const { emailVerified, profile } = (user && user.id && user.attributes) || {};
  const { firstName, publicData, protectedData } = profile || {};
  const { isAdmin } = publicData || {};
  const {
    adminPanel,
    signUpCompleted,
    businessListingId = false,
    businessListingPublished = false,
  } = protectedData || {};
  const { pageCompleted } = (businessListing && businessListing.id && businessListing.attributes.privateData) || {};

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  // const notificationCountBadge =
  //   notificationCount > 0 ? (
  //     <NotificationBadge className={css.notificationBadge} count={notificationCount} />
  //   ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const editListingParams = protectedData && getBusinessListingParams(currentUser);

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <ul>
          {isAuthenticated
            ? <>
              {(isAdmin || adminPanel)
                ? <li>
                  <NamedLink
                    className={classNames(css.navigationLink, currentPageClass('AdminDashboardPage'))}
                    name="AdminDashboardBasePage"
                  >
                    <FormattedMessage id="TopbarMobileMenu.adminPanel" />
                  </NamedLink>
                </li>
                : null}
              {!signUpCompleted || (signUpCompleted && signUpCompleted != SIGNUP_TAB_COMPLETED)
                ? <NamedLink className={css.navigationLink} name="SignUpStepsPage" params={{ tab: "detail" }} >
                  <span className={css.createListing}>
                    <FormattedMessage id={signUpCompleted == SIGNUP_TAB_DETAIL
                      ? 'TopbarDesktop.profileDetail'
                      : 'TopbarDesktop.profileReference'} />
                  </span>
                </NamedLink>
                : signUpCompleted && signUpCompleted == SIGNUP_TAB_COMPLETED
                  ? emailVerified
                    ? businessListingId
                      ? businessListingPublished == BUSINESS_LISTING_TYPE_PUBLISHED
                        ? <>
                          <li>
                            <NamedLink
                              className={css.navigationLink}
                              name="NewListingPage"
                            >
                              <span className={css.createListing}>
                                <FormattedMessage id="TopbarMobileMenu.createRFPMobile" />
                              </span>
                            </NamedLink>
                          </li>
                          <li>
                            <NamedLink
                              className={css.navigationLink}
                              name="DashboardBasePage"
                            >
                              <span className={css.createListing}>
                                <FormattedMessage id="TopbarDesktop.bidderDashboard" />
                              </span>
                            </NamedLink>
                          </li>
                          <li>
                            <NamedLink
                              className={css.navigationLink}
                              name="ProviderDashBoardPage"
                            >
                              <span className={css.createListing}>
                                <FormattedMessage id="TopbarMobileMenu.providerDashboard" />
                              </span>
                            </NamedLink>
                          </li>
                        </>
                        : pageCompleted == 'details'
                          ? <NamedLink className={css.navigationLink} name="EditListingPage" params={editListingParams}>
                            <span className={css.createListing}>
                              <FormattedMessage id={"TopbarDesktop.businessDetail"} />
                            </span>
                          </NamedLink>
                          : <NamedLink className={css.navigationLink} name="EditListingPage" params={editListingParams}>
                            <span className={css.createListing}>
                              <FormattedMessage id={"TopbarDesktop.businessFinal"} />
                            </span>
                          </NamedLink>
                      : <NamedLink className={css.navigationLink} name="EditListingPage" params={editListingParams}>
                        <span className={css.createListing}>
                          <FormattedMessage id={"TopbarDesktop.businessAdd"} />
                        </span>
                      </NamedLink>
                    : <Button
                      className={css.buttonLink}
                      onClick={onResendVerificationEmail}
                      inProgress={sendVerificationEmailInProgress}
                    >
                      <span className={css.createListing}>
                        <FormattedMessage id={"TopbarDesktop.verifyEmail"} />
                      </span>
                    </Button>
                  : null}
              {isAdmin
                ? <li>
                  <NamedLink
                    className={css.navigationLink}
                    name="AdminDashboardBasePage"
                  >
                    <span className={css.createListing}>
                      <FormattedMessage id="TopbarDesktop.adminDashboardPage" />
                    </span>
                  </NamedLink>
                </li>
                : null}
              <li>
                <NamedLink
                  className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
                  name="AccountSettingsPage"
                >
                  <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
                </NamedLink>
              </li>
            </>
            : null}

        </ul>

        <div className={css.spacer} />
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.createRFP" />
        </NamedLink>
      </div> */}
    </div>
  );
};

export default TopbarMobileMenu;